// AppContext.tsx

import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import { ApolloProvider } from "@apollo/client";
import { AuthProvider } from "./useAuth";
import { client } from "../../lib/apolloClient";
import { Cart } from "../../types/cart";

const defaultState: AppState = {
  cart: undefined,
  setCart: () => {},
  wishlist: [],
  setWishlist: () => {},
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  isInWishlist: () => false,
};

const AppContext = createContext(defaultState);

const AppProvider: React.FC<ContextProps> = ({ children }) => {
  const [cart, setCart] = useState<AppState["cart"]>(defaultState.cart);
  const [wishlist, setWishlist] = useState<string[]>(
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("wishlist") || "[]")
      : []
  );

  // Persist to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
    }
  }, [wishlist]);

  const addToWishlist = (slug: string) => {
    setWishlist((prev) => (prev.includes(slug) ? prev : [...prev, slug]));
  };

  const removeFromWishlist = (slug: string) => {
    setWishlist((prev) => prev.filter((s) => s !== slug));
  };

  const isInWishlist = (slug: string) => {
    return wishlist.includes(slug);
  };

  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <AppContext.Provider
          value={{
            cart,
            setCart,
            wishlist,
            setWishlist,
            addToWishlist,
            removeFromWishlist,
            isInWishlist,
          }}>
          {children}
        </AppContext.Provider>
      </AuthProvider>
    </ApolloProvider>
  );
};

const useAppState = () => useContext(AppContext);

type AppState = {
  cart?: Cart;
  setCart: Dispatch<SetStateAction<Cart | undefined>>;

  wishlist: string[]; // Array of product IDs or slugs
  setWishlist: Dispatch<SetStateAction<string[]>>;
  addToWishlist: (slug: string) => void;
  removeFromWishlist: (slug: string) => void;
  isInWishlist: (slug: string) => boolean;
};

type ContextProps = {
  children: React.ReactNode;
};

export { AppProvider, useAppState };
